import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { COMPANY, SERVICES } from "../data/site";

export default function Header({ variant = "light" }) {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const onDark = variant === "dark";

  const linkBase = `text-sm tracking-wide font-medium transition-colors ${onDark ? "text-white/85 hover:text-white" : "text-[#050A14] hover:text-[#0B1426]"}`;

  return (
    <header
      data-testid="site-header"
      className={`${onDark ? "bg-[#0B1426] border-white/10" : "bg-[#F9F8F6] border-[#E5E3DB]"} border-b sticky top-0 z-40 backdrop-blur supports-[backdrop-filter]:bg-opacity-90`}
    >
      <div className="container-cps flex items-center justify-between h-16 md:h-20 gap-2">
        <Link to="/" className="flex md:items-center md:gap-3 min-w-0 flex-col md:flex-row leading-none" data-testid="header-logo-link">
          <span className={`font-display text-[1.35rem] md:text-2xl tracking-tight whitespace-nowrap ${onDark ? "text-white" : "text-[#0B1426]"}`}>
            Connolly's<span style={{ color: "#C5A059" }}>.</span>
          </span>
          <span className={`whitespace-nowrap text-[8px] md:text-xs tracking-[0.22em] uppercase font-semibold mt-0.5 md:mt-0 ${onDark ? "text-white/55" : "text-[#C5A059]"}`}>
            Painting Solutions
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          <NavLink to="/" end className={linkBase} data-testid="nav-home">Home</NavLink>
          <div className="relative group">
            <button className={`${linkBase} flex items-center gap-1`} data-testid="nav-services-trigger">
              Services
            </button>
            <div className={`absolute left-0 top-full pt-3 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity`}>
              <div className="min-w-[280px] bg-white border border-[#E5E3DB] shadow-sm">
                {SERVICES.map((s) => (
                  <Link key={s.slug} to={`/${s.slug}`} className="block px-5 py-3 text-sm text-[#0B1426] hover:bg-[#F9F8F6] border-b border-[#E5E3DB] last:border-0" data-testid={`nav-service-${s.slug}`}>
                    {s.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <NavLink to="/gallery" className={linkBase} data-testid="nav-gallery">Gallery</NavLink>
          <NavLink to="/reviews" className={linkBase} data-testid="nav-reviews">Reviews</NavLink>
          <NavLink to="/suburbs" className={linkBase} data-testid="nav-suburbs">Service Areas</NavLink>
          <NavLink to="/contact" className={linkBase} data-testid="nav-contact">Contact</NavLink>
        </nav>

        {/* Right cluster — phone + quote on desktop, compact icons + button on mobile */}
        <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
          <a href={`tel:${COMPANY.phoneTel}`}
             className={`hidden md:flex items-center gap-2 text-sm ${onDark ? "text-white/80" : "text-[#0B1426]"}`}
             data-testid="header-phone">
            <Phone size={14} />
            <span className="font-medium">{COMPANY.phone}</span>
          </a>
          <a href={`tel:${COMPANY.phoneTel}`}
             aria-label="Call us"
             className={`md:hidden p-2 ${onDark ? "text-white" : "text-[#0B1426]"}`}
             data-testid="header-phone-mobile">
            <Phone size={18} />
          </a>
          <div className="hidden md:block">
            <Link to="/quote" className="btn-gold" data-testid="header-get-quote">Get a Free Quote</Link>
          </div>
          <Link to="/quote"
                className="md:hidden inline-flex items-center"
                style={{ background: "#C5A059", color: "#fff", padding: "8px 14px", fontSize: "11px", letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600 }}
                data-testid="header-get-quote-mobile">Free Quote</Link>
          <button
            aria-label="Toggle menu"
            className={`lg:hidden p-2 -mr-2 ${onDark ? "text-white" : "text-[#0B1426]"}`}
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className={`lg:hidden border-t ${onDark ? "border-white/10 bg-[#0B1426]" : "border-[#E5E3DB] bg-[#F9F8F6]"}`} data-testid="mobile-menu-panel">
          <div className="container-cps py-2">
            {[
              { to: "/", label: "Home", id: "home" },
              { to: "/gallery", label: "Gallery", id: "gallery" },
              { to: "/reviews", label: "Reviews", id: "reviews" },
              { to: "/suburbs", label: "Service Areas", id: "suburbs" },
              { to: "/contact", label: "Contact", id: "contact" },
            ].map((it) => (
              <Link
                key={it.id}
                to={it.to}
                onClick={() => { setOpen(false); setServicesOpen(false); }}
                className={`block py-3.5 text-[15px] font-medium ${onDark ? "text-white" : "text-[#0B1426]"} border-b ${onDark ? "border-white/10" : "border-[#E5E3DB]"}`}
                data-testid={`mobile-nav-${it.id}`}
              >
                {it.label}
              </Link>
            ))}

            {/* Services — collapsible submenu */}
            <button
              type="button"
              onClick={() => setServicesOpen((v) => !v)}
              className={`w-full flex items-center justify-between py-3.5 text-[15px] font-medium ${onDark ? "text-white" : "text-[#0B1426]"} border-b ${onDark ? "border-white/10" : "border-[#E5E3DB]"}`}
              data-testid="mobile-nav-services-toggle"
              aria-expanded={servicesOpen}
            >
              Services
              <ChevronDown
                size={18}
                className={`transition-transform ${servicesOpen ? "rotate-180" : ""} ${onDark ? "text-white/60" : "text-[#4F5B6A]"}`}
              />
            </button>
            {servicesOpen && (
              <div className={`overflow-hidden border-b ${onDark ? "border-white/10" : "border-[#E5E3DB]"}`} data-testid="mobile-services-submenu">
                {SERVICES.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/${s.slug}`}
                    onClick={() => { setOpen(false); setServicesOpen(false); }}
                    className={`block py-3 pl-4 text-[14px] ${onDark ? "text-white/80" : "text-[#4F5B6A]"} hover:text-[#C5A059]`}
                    data-testid={`mobile-nav-service-${s.slug}`}
                  >
                    {s.name}
                  </Link>
                ))}
              </div>
            )}

            <Link
              to="/quote"
              onClick={() => { setOpen(false); setServicesOpen(false); }}
              className={`block py-3.5 text-[15px] font-medium ${onDark ? "text-white" : "text-[#0B1426]"}`}
              data-testid="mobile-nav-quote"
            >
              Get a Free Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
